/** Wall-clock helpers for schedule-timezone date/time inputs (datetime-local values interpreted in an IANA timezone). */

export function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

export function roundUpToMinute(date: Date): Date {
  const normalized = new Date(date.getTime());
  if (normalized.getSeconds() === 0 && normalized.getMilliseconds() === 0) return normalized;
  normalized.setSeconds(0, 0);
  normalized.setMinutes(normalized.getMinutes() + 1);
  return normalized;
}

export function toLocalParts(dateUtc: Date, timezone: string): { year: number; month: number; day: number; hour: number; minute: number; second: number } {
  const dtf = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const parts = dtf.formatToParts(dateUtc);
  const get = (type: string) => Number(parts.find(p => p.type === type)?.value || 0);
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour: get('hour'),
    minute: get('minute'),
    second: get('second'),
  };
}

export function localToUtc(localDateLikeUtc: Date, timezone: string): Date {
  const asIfUtc = localDateLikeUtc.getTime();
  const partsInTz = toLocalParts(new Date(asIfUtc), timezone);
  const tzAsUtc = Date.UTC(partsInTz.year, partsInTz.month - 1, partsInTz.day, partsInTz.hour, partsInTz.minute, partsInTz.second);
  const offset = tzAsUtc - asIfUtc;
  return new Date(asIfUtc - offset);
}

export function toDateTimeLocalInTimezone(value: string, timezone: string): string {
  const d = roundUpToMinute(new Date(value));
  if (Number.isNaN(d.getTime())) return '';
  const parts = toLocalParts(d, timezone || 'UTC');
  return `${parts.year}-${pad2(parts.month)}-${pad2(parts.day)}T${pad2(parts.hour)}:${pad2(parts.minute)}`;
}

export function parseDateTimeLocalInTimezone(value: string, timezone: string): Date {
  if (!value) return new Date(NaN);
  const [datePart, timePart] = value.split('T');
  if (!datePart || !timePart) return new Date(NaN);

  const [yearStr, monthStr, dayStr] = datePart.split('-');
  const [hourStr, minuteStr] = timePart.split(':');
  const year = Number(yearStr);
  const month = Number(monthStr);
  const day = Number(dayStr);
  const hour = Number(hourStr);
  const minute = Number(minuteStr);
  if (![year, month, day, hour, minute].every(Number.isFinite)) return new Date(NaN);

  const localLikeUtc = new Date(Date.UTC(year, month - 1, day, hour, minute, 0));
  return localToUtc(localLikeUtc, timezone || 'UTC');
}
